import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { generateSavedViewUrl, viewDataTypeUnionToEnum } from "components/saved_searches/utils";
import Button from "components/ui/button/Button";
import { useSavedViews } from "lib/hooks/api/saved_views/useSavedViews";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { SavedViewSortBy, SavedViewSortOrder } from "lib/types/graphQLEnums";
import { SavedDocumentView, SavedViewDataTypeUnion, SavedViewType } from "lib/types/savedViews";
import EmptyStatePinnedView from "./EmptyStatePinnedView";

type EmptyStateActionProps = {
  hydratedRecentViews: (SavedViewType | SavedDocumentView | undefined)[];
  setSelectedView: (viewId: string, viewName: string, context: string) => void;
  dataType: SavedViewDataTypeUnion;
};

function EmptyStateAction({
  hydratedRecentViews,
  setSelectedView,
  dataType,
}: EmptyStateActionProps) {
  const { data: pinnedViews, isLoading: isLoadingPinnedViews } = useSavedViews(
    {
      pinned: true,
      orderBy: SavedViewSortBy.PinnedRank,
      order: SavedViewSortOrder.Asc,
      dataTypes: [viewDataTypeUnionToEnum(dataType)],
    },
    { page: 1, pageSize: 50 },
  );

  return (
    <Flex column alignItems="center" gap={24}>
      {hydratedRecentViews.length > 1 && (
        <Flex column>
          {hydratedRecentViews
            .filter((view): view is SavedViewType & { id: string } =>
              Boolean(view && view.id && view.name),
            )
            .slice(0, 2)
            .map((view) => (
              <Button
                buttonType="tertiary"
                key={view.id}
                as="a"
                href={generateSavedViewUrl(view.id, dataType)}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedView(view.id, view.name, "Recent view");
                }}
              >
                {view.name}
              </Button>
            ))}
        </Flex>
      )}
      <Flex column alignItems="center" gap={16}>
        <Text fontWeight="500">Pinned {dataType} searches</Text>
        {isLoadingPinnedViews ? (
          <UIcon icon="spinner" size={32} />
        ) : pinnedViews?.results.length === 0 ? (
          <Flex gap={8} alignItems="center">
            <UIcon icon="pin02" color="sysTextInactive" />
            <Text color="sysTextInactive">Your pinned searches will show here</Text>
          </Flex>
        ) : (
          <PinnedViewContainer flexWrap="wrap" justifyContent="center">
            {pinnedViews?.results.map((view) => (
              <EmptyStatePinnedView
                key={view.id}
                dataType={dataType}
                setSelectedView={setSelectedView}
                id={view.id}
                name={view.name}
                description={view.description ?? undefined}
              />
            ))}
          </PinnedViewContainer>
        )}
      </Flex>
    </Flex>
  );
}

const PinnedViewContainer = styled(Flex)({
  maxWidth: 996,
  columnGap: 16,
  rowGap: 14,
  margin: "8px 24px 0 24px",
});

export default EmptyStateAction;
